import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useMemo, useRef, useState } from 'react'
import Skeleton from '@material-ui/lab/Skeleton'
import { ClickAwayListener } from '@material-ui/core'
import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { DeviceSelectItem, SelectOptions } from '../../Common/Components/Selects/Types'
import { MonitoringStatuses } from '../Constants/Constants'
import Select from '../../Common/Components/Selects/Select'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ManageQueues as ManageQueuesLanguage } from '../../../../i18n'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { deviceURL } from '../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../NonReact/constants/REST'
import {
    AutoAttendantLinkageInfo,
    CallQueueLinkageInfo,
    LINKAGE_TYPES_AUTOATTENDANT,
    LINKAGE_TYPES_CALLQUEUE,
    LINKAGE_TYPES_POLICY,
    PolicyLinkageInfo,
    UpdateQueueRequestBody,
    VoiceAppLinkageInfo
} from '../Types/Types'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { Device, IndividualTelItem, ReactState } from '../../Common/Types/Types'
import Loader from '../../Common/Components/Loader'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { DevicesResponse } from '../../../NonReact/Types/Types'
import PopperBubble from '../../Common/Components/PopperBubble'
import Icon, { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import { ENABLE_REAL_TIME_QUEUES_MANUAL_MODE } from '../../../ReactRouting/Constants/FeatureFlags'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { getDeviceReferences } from '../../../NonReact/common/API/EntityAPIClient'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { $error } from '../../Common/Utils/ConsoleUtils'
import { SelectOption } from '../../Common/Components/Selects/Select/Select'

type Props = {
    callQueue: DeviceSelectItem
    queueError: Definable<string>
    currentTelSys: Nullable<IndividualTelItem>
    telsys: number
    partition: number
    fetchDevicesFromPartition: (
        telsysValue: number,
        partitionValue: number,
        setLoading?: (loading: boolean) => void
    ) => void
    isManualRTEnabled: boolean
}

export const ManageQueueEntry: FC<Props> = ({
    callQueue: { original: originalDevice, additionalDevices },
    queueError,
    currentTelSys,
    telsys,
    partition,
    fetchDevicesFromPartition,
    isManualRTEnabled
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(ManageQueuesLanguage.NAME)

    const { displayError }: MessageContextType = useContext(MessageContext)

    const isManualRTQueuesEnabled: Definable<boolean> = useFeatureFlag(ENABLE_REAL_TIME_QUEUES_MANUAL_MODE)

    const [isDataLoading, setIsDataLoading]: ReactState<boolean> = useState<boolean>(false)
    const [isPopupOpen, setIsPopupOpen]: ReactState<boolean> = useState<boolean>(false)
    const [voiceAppLinkageInfo, setVoiceAppLinkageInfo]: ReactState<Nullable<VoiceAppLinkageInfo[]>> =
        useState<Nullable<VoiceAppLinkageInfo[]>>()
    const [isLinkageInfoLoading, setIsLinkageInfoLoading]: ReactState<boolean> = useState<boolean>(false)

    const popperRef: MutableRefObject<OnlyNullable<HTMLDivElement>> = useRef(null)

    const allOriginalDevices: DevicesResponse[] = useMemo(
        (): DevicesResponse[] =>
            [
                ...(originalDevice ? [originalDevice] : []),
                ...(additionalDevices?.map(({ original }: DeviceSelectItem): Nullable<DevicesResponse> => original) ??
                    [])
            ].filter(isVarNotEmpty),
        [originalDevice, JSON.stringify(additionalDevices)]
    )

    const allQueueStatuses: string[] = useMemo(
        (): string[] =>
            allOriginalDevices.map(({ QueueStatus }: DevicesResponse): string => QueueStatus ?? EMPTY_STRING),
        [JSON.stringify(allOriginalDevices)]
    )

    const updateAllQueues = useCallback(
        (devicesToUpdate: DevicesResponse[], realTime: boolean): Promise<unknown> =>
            Promise.all(
                devicesToUpdate.map(
                    (device: DevicesResponse): Promise<unknown> =>
                        requestJSON(deviceURL(`${telsys}`, `${partition}`, `${device?.DeviceNumber}`), {
                            method: RESTMethods.PUT,
                            body: {
                                DisableRTMonitoring: realTime
                            } as UpdateQueueRequestBody
                        })
                )
            ),
        [telsys, partition]
    )

    const updateQueue = useCallback(
        (realTime: boolean): void => {
            setIsDataLoading(true)
            updateAllQueues(
                [
                    ...(originalDevice ? [originalDevice] : []),
                    ...(additionalDevices
                        ?.map(
                            (additionalDevice: DeviceSelectItem): Nullable<DevicesResponse> =>
                                additionalDevice?.original
                        )
                        ?.filter(isVarNotEmpty) ?? [])
                ],
                realTime
            )
                .then((): void => {
                    setTimeout((): void => {
                        fetchDevicesFromPartition(telsys, partition, setIsDataLoading)
                    }, 300)
                })
                .catch((error: Error): void => {
                    displayError(error)
                    setIsDataLoading(false)
                })
        },
        [telsys, partition, originalDevice?.DeviceNumber]
    )

    const isQueueLoading: boolean = useMemo(
        (): boolean =>
            allQueueStatuses.some(
                (status: string): boolean =>
                    !Object.values(MonitoringStatuses).includes(status) && status !== EMPTY_STRING
            ),
        [allQueueStatuses]
    )

    const isQueueUnknown: boolean = useMemo(
        (): boolean => allQueueStatuses.some((status: string): boolean => status === EMPTY_STRING),
        [allQueueStatuses]
    )

    const isQueuePending: boolean = useMemo(
        (): boolean =>
            allQueueStatuses.some((status: string): boolean =>
                [MonitoringStatuses.ENABLING_MONITORING, MonitoringStatuses.DISABLING_MONITORING].includes(status)
            ),
        [allQueueStatuses]
    )

    const isQueuePendingLanguage: string = useMemo((): string => {
        if (allQueueStatuses.some((status: string): boolean => status === MonitoringStatuses.ENABLING_MONITORING)) {
            return language.get(ManageQueuesLanguage.ManageQueue_Pending_Queue_Enabling)
        }

        if (allQueueStatuses.some((status: string): boolean => status === MonitoringStatuses.DISABLING_MONITORING)) {
            return language.get(ManageQueuesLanguage.ManageQueue_Pending_Queue_Disabling)
        }

        return EMPTY_STRING
    }, [allQueueStatuses])

    const isCompatible: boolean =
        allOriginalDevices.every((device: DevicesResponse): boolean => !!device?.QueueCompatibility) ?? true
    const isRealTimeForced: boolean = originalDevice?.QueueStatus === MonitoringStatuses.REAL_TIME_FORCED

    const realTimeOption: SelectOption<string> =
        !isManualRTEnabled && (!isCompatible || isRealTimeForced)
            ? {
                  label: language.get(ManageQueuesLanguage.ManageQueueCallMonitoringRealTimeForced),
                  value: MonitoringStatuses.REAL_TIME_FORCED,
                  className: 'mq-real-time-forced'
              }
            : {
                  label: language.get(ManageQueuesLanguage.ManageQueueCallMonitoringRealTime),
                  value: MonitoringStatuses.REAL_TIME
              }

    const monitoringStatus: SelectOptions<string> = useMemo(
        (): SelectOptions<string> => [
            {
                label: language.get(ManageQueuesLanguage.ManageQueueCallMonitoringHistoric),
                value: MonitoringStatuses.HISTORIC
            },
            realTimeOption,
            ...(!isQueueUnknown
                ? []
                : [
                      {
                          label: language.get(ManageQueuesLanguage.ManageQueueCallMonitoringUnknown),
                          value: MonitoringStatuses.UNKNOWN
                      }
                  ])
        ],
        [isManualRTEnabled, isCompatible, isQueueUnknown, language]
    )

    const displayMonitoringStatus: ReactElement = useMemo((): ReactElement => {
        if (isQueueLoading) {
            return (
                <div className='queue-pending'>
                    <Loader size={25} />
                </div>
            )
        }

        if (isQueuePending) {
            return (
                <div className='queue-pending'>
                    <Loader size={25} />
                    {isQueuePendingLanguage}
                </div>
            )
        }

        const isDisabled: Nullable<boolean> =
            currentTelSys?.HistoricOnly || isQueueUnknown || currentTelSys?.BotsDisabled || !currentTelSys?.CommsEnabled

        return (
            <Select
                classNames={isRealTimeForced ? 'mq-real-time-forced' : ''}
                value={originalDevice?.QueueStatus ?? MonitoringStatuses.UNKNOWN}
                options={monitoringStatus}
                renderSelectedValue={false}
                disabled={isDisabled}
                rules={{
                    required: true
                }}
                handleChange={(value: string): void => {
                    updateQueue(value === MonitoringStatuses.HISTORIC)
                }}
            />
        )
    }, [
        isQueueLoading,
        isQueuePending,
        isQueuePendingLanguage,
        isRealTimeForced,
        monitoringStatus,
        JSON.stringify(originalDevice),
        JSON.stringify(currentTelSys)
    ])

    const deviceDisplay = (origDev?: Device): ReactElement => (
        <div className='mq-device-display' key={`device-display-${origDev?.DeviceNumber}`}>
            <div className='mq-col'>
                <span className='mq-col-label'>
                    {origDev?.DeviceNumber}&nbsp;-&nbsp;{origDev?.Description}
                </span>
            </div>

            {!isManualRTEnabled ? (
                <div className='mq-col'>
                    <span className='mq-col-label'>
                        {isCompatible
                            ? language.get(ManageQueuesLanguage.ManageQueueCallCQConfigurationCompatible)
                            : language.get(ManageQueuesLanguage.ManageQueueCallCQConfigurationIncompatible)}
                    </span>
                </div>
            ) : (
                <></>
            )}

            <div className='mq-col'>
                <span className='mq-col-label'>{origDev?.TelSysUserID}</span>
            </div>
        </div>
    )

    const getLinkageInfo = (): void => {
        setIsLinkageInfoLoading(true)

        if (
            isStringNotEmpty(originalDevice?.IDOnTelSysOwner) &&
            isManualRTQueuesEnabled &&
            isVarNotEmpty(originalDevice)
        ) {
            getDeviceReferences(telsys, partition, originalDevice.IDOnTelSysOwner)
                .then((linkageInfo: Nullable<VoiceAppLinkageInfo[]>): void => setVoiceAppLinkageInfo(linkageInfo))
                .catch((e: Error): void => {
                    $error(e)
                    setVoiceAppLinkageInfo(null)
                })
                .finally((): void => setIsLinkageInfoLoading(false))
        }
    }

    return (
        <>
            <div className={`mq-entry ${isDataLoading ? 'hide' : ''}`}>
                <div className='mq-main-content'>
                    <div>
                        {deviceDisplay(originalDevice)}
                        {additionalDevices?.map(({ original: origDev }: DeviceSelectItem) => deviceDisplay(origDev))}
                    </div>

                    <div className='mq-col-monitoring'>
                        <div className='mq-entry-field'>{displayMonitoringStatus}</div>
                    </div>

                    {isManualRTQueuesEnabled ? (
                        <>
                            <IconButton
                                type={IconButtonType.BUTTON}
                                onClick={(): void => {
                                    setIsPopupOpen((current: boolean): boolean => !current)
                                    getLinkageInfo()
                                }}
                                iconName={IconName.INFO}
                                ref={popperRef}
                            />

                            <PopperBubble
                                className='mq-linkage'
                                content={
                                    <ClickAwayListener onClickAway={(): void => setIsPopupOpen(false)}>
                                        <div>
                                            <strong className='mq-linkage-title'>
                                                {language.get(ManageQueuesLanguage.ManageQueues_Linkage_Title)}
                                            </strong>
                                            <div>
                                                {/* eslint-disable-next-line no-nested-ternary */}
                                                {isLinkageInfoLoading ? (
                                                    <Loader />
                                                ) : ArrayUtils.isEmpty(voiceAppLinkageInfo) ? (
                                                    language.get(ManageQueuesLanguage.ManageQueues_Linkage_NoInfo)
                                                ) : (
                                                    voiceAppLinkageInfo?.map(
                                                        (
                                                            linkageInfo: VoiceAppLinkageInfo,
                                                            index: number
                                                        ): ReactElement => {
                                                            const { type }: VoiceAppLinkageInfo = linkageInfo
                                                            const typeLowerCase: string = type.toLowerCase()

                                                            let linkageDescription: ReactElement = <></>

                                                            if (typeLowerCase === LINKAGE_TYPES_CALLQUEUE) {
                                                                const {
                                                                    exception,
                                                                    resourceAccountUpn,
                                                                    name
                                                                }: CallQueueLinkageInfo =
                                                                    linkageInfo as CallQueueLinkageInfo

                                                                linkageDescription = LanguageUtils.formatLanguageEntry(
                                                                    language.getInterpolated(
                                                                        ManageQueuesLanguage.ManageQueues_Linkage_Types_CallQueue,
                                                                        resourceAccountUpn,
                                                                        language.get(
                                                                            `ManageQueues_Linkage_CallQueue_Exceptions_${exception.toLowerCase()}`
                                                                        ),
                                                                        name
                                                                    )
                                                                )
                                                            } else if (typeLowerCase === LINKAGE_TYPES_AUTOATTENDANT) {
                                                                const {
                                                                    menu,
                                                                    option,
                                                                    resourceAccountUpn,
                                                                    name
                                                                }: AutoAttendantLinkageInfo =
                                                                    linkageInfo as AutoAttendantLinkageInfo

                                                                linkageDescription = LanguageUtils.formatLanguageEntry(
                                                                    language.getInterpolated(
                                                                        ManageQueuesLanguage.ManageQueues_Linkage_Types_AutoAttendant,
                                                                        resourceAccountUpn,
                                                                        language.get(
                                                                            `ManageQueues_Linkage_Options_${option.toLowerCase()}`
                                                                        ) ?? option.replace('Tone', EMPTY_STRING),
                                                                        language.get(
                                                                            `ManageQueues_Linkage_AutoAttendant_Menu_Options_${menu.toLowerCase()}`
                                                                        ),
                                                                        name
                                                                    )
                                                                )
                                                            } else if (typeLowerCase === LINKAGE_TYPES_POLICY) {
                                                                const {
                                                                    policyId,
                                                                    resourceAccountUpn,
                                                                    policyType
                                                                }: PolicyLinkageInfo = linkageInfo as PolicyLinkageInfo

                                                                linkageDescription = LanguageUtils.formatLanguageEntry(
                                                                    language.getInterpolated(
                                                                        ManageQueuesLanguage.ManageQueues_Linkage_Types_Policy,
                                                                        resourceAccountUpn,
                                                                        policyId,
                                                                        language.get(
                                                                            `ManageQueues_Linkage_Policy_Types_${policyType.toLowerCase()}`
                                                                        )
                                                                    )
                                                                )
                                                            }

                                                            return (
                                                                <div
                                                                    className='mq-linkage-info'
                                                                    key={`linkage-info-${index}`}
                                                                >
                                                                    <span className='mq-linkage-name'>
                                                                        {linkageDescription}
                                                                    </span>
                                                                </div>
                                                            )
                                                        }
                                                    )
                                                )}
                                            </div>
                                            <span className='mq-linkage-help'>
                                                <Icon
                                                    className='mq-linkage-help-icon'
                                                    iconName={IconName.QUESTION_MARK}
                                                />
                                                {language.get(ManageQueuesLanguage.ManageQueues_Linkage_Help)}
                                            </span>
                                        </div>
                                    </ClickAwayListener>
                                }
                                open={isPopupOpen}
                                anchorEl={popperRef.current}
                                placement='top'
                                infoMode
                            />
                        </>
                    ) : (
                        <></>
                    )}
                </div>

                {!IsStringEmpty(queueError) ? <div className='mq-error-message'>{queueError}</div> : <></>}
            </div>

            {isDataLoading ? <Skeleton /> : <></>}
        </>
    )
}
